import { DialogTitle } from '@mui/material'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'

import { DialogDefaultHeader } from '#/components/Dialog/DefaultHeader'

import { DialogStyled } from './styles'
import { DialogOptions } from './types'

export type { DialogOptions } from './types'

/** Propriedades padrão para o Dialog */
export const defaultDialogOptions: DialogOptions = {
  fullWidth: true,
  maxWidth: 'sm',
  open: false,
  withBottomClose: true,
}

/**
 * Controlador central do Dialog, usado para abrir, fechar e atualizar as opções do Dialog dinamicamente.
 */
class DialogController {
  private updateDialogOptions: Dispatch<SetStateAction<DialogOptions>> | null = null

  /** Inicializa o controlador com o setter de estado */
  initialize = (updateFn: Dispatch<SetStateAction<DialogOptions>>) => {
    this.updateDialogOptions = updateFn
  }

  /** Aguarda o updateDialogOptions ser inicializado */
  waitForInitialize = async () => {
    return new Promise(resolve => {
      const check = () => {
        if (this.updateDialogOptions) {
          resolve(true)
          clearInterval(interval)
        }
      }
      const interval = setInterval(check, 100)
    })
  }

  /** Abre o Dialog com as opções fornecidas */
  open = (options: Omit<DialogOptions, 'open'>) => {
    this.options({ ...options, open: true })
  }

  /** Fecha o Dialog */
  close = () => {
    this.options(defaultDialogOptions)
  }

  /** Atualiza as opções do Dialog sem necessariamente abrir ou fechar */
  options = async (options: DialogOptions) => {
    await this.waitForInitialize()
    this.updateDialogOptions?.({ ...defaultDialogOptions, ...options })
  }

  /** Atualiza parcialmente as opções do Dialog aberto, preservando as demais (não remonta o conteúdo) */
  update = async (options: Partial<DialogOptions>) => {
    await this.waitForInitialize()
    this.updateDialogOptions?.(previous => ({ ...previous, ...options }))
  }
}

export const DialogComponent = ({
  onClose,
  options,
}: {
  onClose: VoidFunction
  options: DialogOptions
}) => {
  const { dismissable = true } = options

  const handleClose = (_?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    // Desabilita clique fora do dialog
    if (!dismissable) return
    if (reason === 'backdropClick' && options.backdropDismissable === false) return

    options.onClose?.()
    onClose()
  }

  if (!options.open) return null

  return (
    <DialogStyled
      open={options.open}
      fullWidth={options.fullWidth}
      maxWidth={options.maxWidth}
      onClose={handleClose}
      sx={{ zIndex: options.zIndex }}
    >
      <DialogTitle sx={options.header?.sx} color={options.header?.level}>
        <DialogDefaultHeader
          {...(options.header ?? {})}
          handleClose={handleClose}
          dismissable={dismissable}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {options.content}
        {options.footer && !options.stickyFooter && options.footer}
      </DialogContent>
      {options.footer && options.stickyFooter && (
        <DialogActions sx={{ px: 3, py: 2 }}>{options.footer}</DialogActions>
      )}
    </DialogStyled>
  )
}

/**
 * Componente Dialog global que renderiza seu conteúdo com base nas opções fornecidas pelo DialogController.
 */
export const DialogContainer = () => {
  const [options, setOptions] = useState<DialogOptions>(defaultDialogOptions)

  useEffect(() => {
    Dialog.initialize(setOptions)
  }, [])

  return <DialogComponent options={options} onClose={() => Dialog.close()} />
}

/** Instância global do controlador do Dialog */
export const Dialog = new DialogController()
